import { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { errorMessage } from '../lib/format';
import { useTable } from '../lib/useTable';
import type { Holiday } from '../lib/types';
import { useAuth } from '../auth/AuthProvider';
import { PageHead } from '../components/AppLayout';
import { NoClassDayDialog } from '../components/NoClassDayDialog';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  getLiabilityTemplate,
  removeLiabilityTemplate,
  templateSignedUrl,
  uploadLiabilityTemplate,
  type LiabilityTemplate,
} from '../lib/documents';

export function SettingsPage() {
  const { session } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const { rows, loading, error, reload } = useTable<Holiday>('holidays', 'holiday_date');
  const [adding, setAdding] = useState(false);

  async function remove(row: Holiday) {
    const ok = await confirm({
      title: `Remove ${format(parseISO(row.holiday_date), 'MMM d')} — "${row.label}"?`,
      body: "Classes that were moved for this day won't move back; this just allows classes on the day again.",
      confirmLabel: 'Remove day',
      danger: true,
    });
    if (!ok) return;
    const { error } = await supabase.from('holidays').delete().eq('id', row.id);
    if (error) toast.error(errorMessage(error));
    else {
      toast.success('No-class day removed.');
      void reload();
    }
  }

  return (
    <>
      <PageHead
        title="Settings"
        subtitle="No-class days, the liability waiver, and your account."
      />

      <div className="card">
        <div className="row between wrap" style={{ marginBottom: '0.3rem' }}>
          <h2 style={{ margin: 0 }}>Holidays &amp; no-class days</h2>
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            + Add no-class day
          </button>
        </div>
        <p className="muted small">
          Days when the academy is closed. They show on the calendar in red, new classes skip them
          automatically, and adding one slides any affected classes a week later. You can also add
          one by clicking a day on the <strong>Calendar</strong>.
        </p>

        <div style={{ marginTop: '0.8rem' }}>
          {loading ? (
            <div style={{ padding: '0.2rem 0' }}>
              <span className="skel" style={{ width: '55%' }} />
              <span className="skel" style={{ width: '40%' }} />
            </div>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : rows.length === 0 ? (
            <p className="muted small">No no-class days added yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {rows.map((h) => (
                    <tr key={h.id}>
                      <td style={{ fontWeight: 600, width: 180, whiteSpace: 'nowrap' }}>
                        {format(parseISO(h.holiday_date), 'EEE MMM d, yyyy')}
                      </td>
                      <td>{h.label}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => void remove(h)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <LiabilityTemplateCard />

      <div className="card">
        <h2>Account</h2>
        <p className="muted small">Signed in as</p>
        <p style={{ fontWeight: 600, margin: 0 }}>{session?.user.email}</p>
      </div>

      {adding && (
        <NoClassDayDialog
          onClose={() => setAdding(false)}
          onChanged={() => {
            setAdding(false);
            void reload();
          }}
        />
      )}
    </>
  );
}

// Upload/manage the blank liability waiver that the "Email waiver" button
// on the Clients page attaches. Stored in the private documents bucket.
function LiabilityTemplateCard() {
  const toast = useToast();
  const confirm = useConfirm();
  const fileInput = useRef<HTMLInputElement>(null);
  const [template, setTemplate] = useState<LiabilityTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getLiabilityTemplate()
      .then(setTemplate)
      .catch(() => setTemplate(null))
      .finally(() => setLoading(false));
  }, []);

  async function onFilePicked(file: File | null) {
    if (!file) return;
    setBusy(true);
    try {
      const next = await uploadLiabilityTemplate(file);
      setTemplate(next);
      toast.success('Waiver template saved.');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function view() {
    if (!template) return;
    const w = window.open('', '_blank');
    try {
      const url = await templateSignedUrl(template.path);
      if (w) w.location.href = url;
    } catch (e) {
      w?.close();
      toast.error(errorMessage(e));
    }
  }

  async function remove() {
    const ok = await confirm({
      title: 'Remove the waiver template?',
      body: 'The “Email waiver” button will be disabled until you upload a new one.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await removeLiabilityTemplate();
      setTemplate(null);
      toast.success('Waiver template removed.');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Liability waiver</h2>
      <p className="muted small">
        Upload the blank waiver you send to new clients. The <strong>Email waiver</strong> button on
        the Clients page attaches this file and marks the client "Sent".
      </p>
      <input
        ref={fileInput}
        type="file"
        accept=".pdf,application/pdf,.doc,.docx,image/*"
        style={{ display: 'none' }}
        onChange={(e) => void onFilePicked(e.target.files?.[0] ?? null)}
      />
      {loading ? (
        <span className="skel" style={{ width: '45%', marginTop: '0.4rem' }} />
      ) : template ? (
        <div className="row wrap" style={{ marginTop: '0.6rem' }}>
          <span className="pill">📎 {template.filename}</span>
          <span className="muted small">
            added {format(parseISO(template.uploaded_at), 'MMM d, yyyy')}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => void view()}>
            View
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
          >
            {busy ? 'Uploading…' : 'Replace…'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--danger)' }}
            onClick={() => void remove()}
            disabled={busy}
          >
            Remove
          </button>
        </div>
      ) : (
        <div style={{ marginTop: '0.6rem' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
          >
            {busy ? 'Uploading…' : '📎 Upload blank waiver…'}
          </button>
        </div>
      )}
    </div>
  );
}
